import { Box, Button, Icon, Theme, Typography } from "@outschool/backpack";
import { ActivityEnrollableTimesModal_ActivityFragment } from "@outschool/gql-frontend-generated";
import { faCalendar, faCalendarCircleExclamation } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import { gql } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import {
  AvailableDateSelector,
  useEsaAvailability
} from "@outschool/ui-components-website";
import { Loading, Modal, Select } from "@outschool/ui-legacy-component-library";
import lodashGroupBy from "lodash/groupBy";
import lodashMap from "lodash/map";
import React, { useEffect } from "react";

import ActivityEnrollableTimesFooter from "./ActivityEnrollableTimesFooter";
import {
  default as AvailableTimeSelector,
  PADDING_X_DESKTOP,
  PADDING_X_MOBILE
} from "./AvailableTimeSelector";

export interface ActivityEnrollableTimesModalProps {
  activityUid: string;
  fetchData: (
    uid: string
  ) => Promise<ActivityEnrollableTimesModal_ActivityFragment>;
  timeZone: string;
  enrollmentUid?: string;
  onEnrollClick?: (args: {
    startTime: Date;
    enrollmentUid?: string;
    orgTeacherUid?: string;
  }) => void;
  onRequestAnotherTimeClick?: () => void;
  shouldHideAlert?: boolean;
  shouldOpenModal?: boolean;
  onClose?: () => void;
  hideTeacherSelect?: boolean;
  hideOpenModalButton?: boolean;
  initialTeacherSelectedUid?: string | null;
  headerText?: string | null;
}

export default function ActivityEnrollableTimesModal({
  activityUid,
  fetchData,
  timeZone,
  enrollmentUid,
  onEnrollClick,
  onRequestAnotherTimeClick,
  shouldHideAlert = false,
  shouldOpenModal,
  onClose,
  hideOpenModalButton,
  initialTeacherSelectedUid = null,
  hideTeacherSelect,
  headerText
}: ActivityEnrollableTimesModalProps) {
  const today = new Date();
  const { isEsaSession } = useSession();
  const [activity, setActivity] = React.useState<
    ActivityEnrollableTimesModal_ActivityFragment | undefined
  >(undefined);

  const [teacherSelectedUid, setTeacherSelectedUid] = React.useState<
    string | null
  >(initialTeacherSelectedUid);
  const teacherOptions: { value: string | null; label: string }[] =
    React.useMemo(() => {
      // check whether the enrollableTimes are for org teachers or individual teachers
      // if they are for individual teachers, we don't want to show the teacher select
      const hasOrgTeachers = activity?.enrollableTimes?.some(enrollableTime => {
        return !!enrollableTime.teacher?.teacherForSellerOrg?.uid;
      });
      if (!hasOrgTeachers) {
        return [];
      }
      const obj = (activity?.enrollableTimes ?? []).reduce((obj, et) => {
        const { uid, name } = et.teacher;
        if (!!uid && !!name) {
          obj[uid] = name;
        }
        return obj;
      }, {} as Record<string, string>);
      return [
        { value: null, label: "All teachers" },
        ...Object.keys(obj).map(key => ({
          value: key,
          label: obj[key]
        }))
      ];
    }, [activity?.enrollableTimes]);
  const enrollableTimesFiltered = React.useMemo(() => {
    if (!teacherSelectedUid) {
      return activity?.enrollableTimes;
    }
    return (activity?.enrollableTimes ?? []).filter(
      et => et.teacher.uid === teacherSelectedUid
    );
  }, [activity?.enrollableTimes, teacherSelectedUid]);

  useEffect(() => {
    if (typeof window !== undefined) {
      fetchData(activityUid).then(a => setActivity(a));
    }
  }, [activityUid, fetchData]);

  useEffect(() => {
    setSelectedDateTime(firstAvailableDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity]);

  useEffect(
    () => setTeacherSelectedUid(initialTeacherSelectedUid),
    [initialTeacherSelectedUid, setTeacherSelectedUid]
  );

  const loading = !activity;

  const { t } = useTranslation(
    "client\\components\\detailPage\\activityEnrollableTimes\\ActivityEnrollableTimesModal"
  );

  const [isOpen, setIsOpen] = React.useState(shouldOpenModal || false);

  useEffect(() => {
    if (shouldOpenModal && !isOpen) {
      setIsOpen(true);
    }
  }, [shouldOpenModal, isOpen]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (isOpen && queryParams.has("unselectTime")) {
      queryParams.delete("unselectTime");
      setHasSelectedTime(false);
    }
  }, [isOpen]);

  const [selectedDateTime, setSelectedDateTime] = React.useState<Date>(today);
  const [hasSelectedTime, setHasSelectedTime] = React.useState<boolean>(false);
  const startOfDateString = React.useCallback(
    (date: Date) => dayjs(date).tz(timeZone).startOf("date").toISOString(),
    [timeZone]
  );
  const availableTimesGroupedByDate = React.useMemo(
    () =>
      lodashGroupBy(
        lodashMap(enrollableTimesFiltered, "startTime"),
        startTime => startOfDateString(startTime)
      ),
    [enrollableTimesFiltered, startOfDateString]
  );

  const trackingLabel = "activity_enrollable_times";
  const trackEvent = useTrackEvent();
  const {
    filterDatesByEsaBookingWindow,
    shouldBlockEsaPurchase,
    blockedEsaPurchaseMessage
  } = useEsaAvailability(activity);

  const hasNoAvailabilities = enrollableTimesFiltered?.length === 0;
  const availableDates = isEsaSession
    ? filterDatesByEsaBookingWindow(Object.keys(availableTimesGroupedByDate))
    : Object.keys(availableTimesGroupedByDate);

  const firstAvailableDate: Date =
    availableDates.length === 0
      ? today
      : dayjs.min(availableDates.map(date => dayjs(date)))?.toDate() ?? today;
  const selectedEnrollableTime = enrollableTimesFiltered?.find(
    e => e.startTime === selectedDateTime.toISOString()
  );
  const availableTimesForSelectedDate = availableDates.includes(
    startOfDateString(selectedDateTime)
  )
    ? availableTimesGroupedByDate[startOfDateString(selectedDateTime)] || []
    : [];

  const handleDayClick = (date: Date) => {
    setSelectedDateTime(date);
    setHasSelectedTime(false);
    trackEvent(`${trackingLabel}_date_change`, {
      activityUid,
      selectedDate: date
    });
  };

  const handleTimeChange = (date: Date) => {
    setSelectedDateTime(date);
    setHasSelectedTime(true);
    trackEvent(`${trackingLabel}_time_change`, {
      activityUid,
      selectedTime: date
    });
  };

  const handleModalOpen = () => {
    fetchData(activityUid).then(a => setActivity(a));
    setIsOpen(true);
    trackEvent(`${trackingLabel}_open`, {
      activityUid
    });
  };

  const handleModalClose = () => {
    if (onClose) {
      onClose();
    }
    setIsOpen(false);
    trackEvent(`${trackingLabel}_close`, {
      activityUid
    });
  };

  const handleOnEnrollClick = React.useCallback(() => {
    if (!hasSelectedTime || !onEnrollClick) {
      return;
    }
    setIsOpen(false);
    const orgTeacherUid =
      teacherOptions?.length > 1
        ? selectedEnrollableTime?.teacher.uid
        : undefined;
    onEnrollClick({
      startTime: dayjs(selectedEnrollableTime?.startTime).toDate(),
      enrollmentUid,
      orgTeacherUid
    });
    trackEvent(`${trackingLabel}_enroll`, {
      activityUid,
      selectedDate: selectedEnrollableTime?.startTime
    });
  }, [
    activityUid,
    enrollmentUid,
    onEnrollClick,
    selectedEnrollableTime,
    teacherOptions,
    trackEvent,
    hasSelectedTime
  ]);

  if (!activity?.hasTeacherSchedule) {
    return null;
  }

  if (hasNoAvailabilities && !hideOpenModalButton) {
    return (
      <>
        {!shouldHideAlert && (
          <Box flex sx={{ flexBasis: "100%" }}>
            <Typography
              sx={{
                backgroundColor: "#fffaea",
                borderColor: "warning.500",
                borderRadius: "0.25em",
                borderStyle: "solid",
                borderWidth: "1px",
                padding: "0.5em"
              }}
            >
              <Icon
                icon={faCalendar}
                color="warning"
                sx={{
                  paddingRight: "0.25em"
                }}
              />
              {!!onRequestAnotherTimeClick
                ? t`Lesson available on request only.`
                : t`There are no available time slots for this class.`}
            </Typography>
          </Box>
        )}
        {!!onRequestAnotherTimeClick ? (
          <Button
            variant="contained"
            onClick={() => {
              onRequestAnotherTimeClick();
              handleModalClose();
            }}
          >{t`Request a Meeting`}</Button>
        ) : (
          <Button variant="contained" disabled>
            {!!enrollmentUid ? t`Add Meeting` : t`Check Availability`}
          </Button>
        )}
      </>
    );
  }

  const headerStr: string = headerText ?? t`Select a start date`;

  return (
    <>
      {shouldBlockEsaPurchase && (
        <Box flex sx={{ flexBasis: "100%" }}>
          <Typography
            sx={{
              backgroundColor: "#fffaea",
              borderColor: "warning.500",
              borderRadius: "0.25em",
              borderStyle: "solid",
              borderWidth: "1px",
              padding: "0.5em"
            }}
          >
            <Icon
              icon={faCalendar}
              color="warning"
              sx={{
                paddingRight: "0.25em"
              }}
            />
            {blockedEsaPurchaseMessage}
          </Typography>
        </Box>
      )}
      {!hideOpenModalButton && (
        <Button
          variant="contained"
          sx={{
            marginY: "1em"
          }}
          onClick={handleModalOpen}
          disabled={loading || shouldBlockEsaPurchase}
        >
          {!!enrollmentUid ? t`Add Meeting` : t`Check Availability`}
        </Button>
      )}
      <Modal
        open={isOpen}
        onClose={handleModalClose}
        ariaLabel={headerStr}
        sx={{ padding: 0 }}
      >
        <Typography
          sx={(theme: Theme) => ({
            paddingTop: "1em",
            paddingBottom: "0.5em",
            paddingX: PADDING_X_MOBILE,
            [theme.breakpoints.up("md")]: {
              paddingX: PADDING_X_DESKTOP
            }
          })}
          variant="h4"
        >
          {headerStr}
        </Typography>
        {teacherOptions.length > 1 && !hideTeacherSelect && (
          <Select
            handleChangeOnBlur={false}
            onChange={setTeacherSelectedUid}
            options={teacherOptions}
            sx={{ marginBottom: "1em", marginX: "1.5em" }}
            value={teacherSelectedUid}
          />
        )}
        {loading ? (
          <Loading />
        ) : (
          <>
            <Box
              sx={(theme: Theme) => ({
                display: "grid",
                gridTemplateColumns: "auto",
                gridTemplateAreas:
                  '"date-selector" "time-selector" "request-another-time"',
                gap: "1em",
                [theme.breakpoints.up("md")]: {
                  gridTemplateColumns: "min-content auto",
                  gridTemplateAreas:
                    '"date-selector time-selector" "request-another-time time-selector"',
                  paddingX: PADDING_X_DESKTOP,
                  gap: "1.5em",
                  maxHeight: "500px"
                }
              })}
            >
              <AvailableDateSelector
                availableDates={availableDates}
                today={today}
                firstAvailableDate={firstAvailableDate}
                selectedDate={selectedDateTime}
                timeZone={timeZone}
                startOfDateString={startOfDateString}
                handleDayClick={handleDayClick}
              />
              {!!onRequestAnotherTimeClick && (
                <Box
                  flex
                  sx={(theme: Theme) => ({
                    gridArea: "request-another-time",
                    paddingX: PADDING_X_MOBILE,
                    paddingBottom: "1.5em",
                    alignItems: "flex-start",
                    gap: "0.25em",
                    [theme.breakpoints.up("md")]: {
                      paddingX: "0"
                    }
                  })}
                >
                  <Typography variant="body1">
                    <Icon icon={faCalendarCircleExclamation} />
                  </Typography>
                  <Box
                    flex
                    sx={{
                      flexDirection: "column",
                      gap: "0.25em",
                      alignItems: "flex-start"
                    }}
                  >
                    <Typography variant="body1">
                      {t`Don’t see what you’re looking for?`}
                    </Typography>
                    <Button
                      variant="link"
                      onClick={() => {
                        onRequestAnotherTimeClick();
                        handleModalClose();
                      }}
                    >{t`Request Another Time.`}</Button>
                  </Box>
                </Box>
              )}
              <AvailableTimeSelector
                availableTimes={availableTimesForSelectedDate}
                selectedDateTime={selectedDateTime}
                hasSelectedTime={hasSelectedTime}
                timeZone={timeZone}
                handleTimeChange={handleTimeChange}
              />
            </Box>
            {onEnrollClick && (
              <ActivityEnrollableTimesFooter
                selectedDateTime={selectedDateTime}
                selectedEnrollableTime={selectedEnrollableTime}
                timeZone={timeZone}
                hasSelectedTime={hasSelectedTime}
                continueButtonText={t`Select`}
                handleCancelClick={handleModalClose}
                handleContinueClick={handleOnEnrollClick}
                showTeacher={teacherOptions?.length > 1}
              />
            )}
          </>
        )}
      </Modal>
    </>
  );
}

ActivityEnrollableTimesModal.fragments = {
  activity: gql`
    fragment ActivityEnrollableTimesModal_activity on Activity {
      uid
      hasTeacherSchedule
      enrollableTimes(
        startAfter: $startAfter
        endBy: $endBy
        filterByDow: $filterByDow
        recurringOnly: $recurringOnly
      ) {
        startTime
        endTime
        teacher {
          uid
          name
          teacherForSellerOrg {
            uid
          }
          hasBackgroundCheckExpired
        }
      }
      autoSchedulingDaysNoticeMin
      autoSchedulingDaysNoticeMax
      esaStatus(esaState: $esaState)
      is_ongoing_weekly
      isClub
      isSelfPaced
      duration_weeks
    }
  `
};
